/**
 * Realtime desktop URL construction — pure helpers (2026-08-03).
 *
 * Split out of useRealtimeDesktopTarget so the URL contract is unit-testable
 * without booting firebase. No firebase/react imports may be added here.
 *
 * HISTORY — why the base URL is now echoed verbatim: this module used to run a
 * client-side "stale host" rewrite table that remapped ~12 hardcoded IPs onto a
 * hardcoded Fernando host. By the time it was removed, the rewrite TARGET
 * (35.238.149.88) was itself a RELEASED ephemeral GCP IP — and because sslip.io
 * resolves any dashed-IP hostname to that IP with no ownership check, whoever
 * held it could serve content that got iframed inside an authenticated
 * prest0.ai/desktop page carrying the viewer's accountId and uid. The table also
 * listed the LIVE internal VM IP (self-loop), and whenever it fired it silently
 * killed AvatarBridge, whose allowed origin derives from the UN-rewritten
 * baseUrl. Stale-host correction belongs server-side: scripts/rollover-vm-ip.js
 * rewrites every URL field on an IP move (selfHealDedicatedVmRoutingDocs only
 * repairs STRUCTURALLY broken docs — a stale-but-well-formed host is not in
 * its remit, so the rollover script is the real mechanism).
 */

export function trimTrailingSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/**
 * Constrain what /desktop is willing to embed for an account.
 *
 * accounts/{id}/vm/main is NOT server-only — firestore.rules allows the
 * account owner to write it — so this doc is semi-trusted input that ends up
 * as an iframe src inside an authenticated page carrying accountId + uid. A
 * scheme check alone would still admit any https host, so mirror the SHAPE the
 * server actually writes (routingDocs.ts builds `https://${dashed-ip}.sslip.io`
 * and validates it with the same dashed-sslip pattern): https, a
 * dashed-IP sslip.io host or a prest0.ai host, and no embedded credentials.
 *
 * Applied ONLY to the Firestore-sourced account branch; the internal branch
 * keeps its `window.PREST0_REALTIME_INTERNAL_BASE` staging override.
 */
const DASHED_SSLIP_HOST = /^\d{1,3}(?:-\d{1,3}){3}\.sslip\.io$/i;

export function isSafeAccountVmBase(baseUrl: string): boolean {
  try {
    const u = new URL(baseUrl);
    if (u.protocol !== 'https:') return false;
    if (u.username || u.password) return false;
    return DASHED_SSLIP_HOST.test(u.hostname)
      || u.hostname === 'prest0.ai'
      || u.hostname.endsWith('.prest0.ai');
  } catch {
    return false;
  }
}

/**
 * Per-page-load cache buster. The realtime app HTML is edited live on each VM
 * (Caddy serves `/public/preston-realtime.html` directly from disk), so a short
 * fingerprint that changes per session forces a fresh fetch on every /desktop
 * visit. Bucketed to ~30s so refreshes within one conversation reuse bfcache.
 */
export function realtimeCacheBuster(nowMs: number = Date.now()): string {
  return Math.floor(nowMs / 30000).toString(36);
}

/** The query parameter the cache buster rides in. */
const CACHE_BUSTER_PARAM = 'v';

/**
 * PURE. The frame address to keep when a re-resolve comes back (2026-10-17).
 *
 * The live conversation with Prest0n's voice runs INSIDE the frame, and coming back to a tab
 * that was hidden for a minute re-resolves the target (useDesktopSession's returning-tab check).
 * The cache buster above turns every 30 s, so the rebuilt address always differed from the one
 * on screen and the frame always reloaded: on 2026-10-17 Prest0n was cut off ten seconds into
 * reading an answer, and the page came back silent, waiting for a tap (Justin: "he kind of cut
 * off and like turned off"). A re-resolve that finds the same workstation now keeps the address
 * on screen; one that differs in anything but the buster (a new host, a new `vm`) still reloads.
 */
export function keptFrameAddress(current: string | null, next: string | null): string | null {
  if (!current || !next || current === next) return next;
  try {
    const a = new URL(current);
    const b = new URL(next);
    a.searchParams.delete(CACHE_BUSTER_PARAM);
    b.searchParams.delete(CACHE_BUSTER_PARAM);
    a.searchParams.sort();
    b.searchParams.sort();
    return a.toString() === b.toString() ? current : next;
  } catch {
    return next;
  }
}

/**
 * Compose the iframe URL. The host is echoed verbatim — see file header.
 *
 * `vm` tells the page WHERE ITS WORKSTATION IS (P2 of
 * docs/PLAN-stateless-workstation-20260911.md). Today it is redundant — the
 * page is served BY that workstation, so it would resolve the same origin from
 * `location` — and it is passed anyway so the parse-and-validate path is
 * exercised in production BEFORE the page moves to the CDN lane and starts
 * depending on it. The page re-validates it with the verbatim twin of
 * isSafeAccountVmBase() and ignores an unsafe value rather than calling it, so
 * this parameter can never point the browser-service bearer somewhere new.
 */
export function buildTargetUrl(baseUrl: string): string {
  const cleanBase = trimTrailingSlash(baseUrl);
  // No accountId and no uid (S1b-3, 2026-09-27). The page used to key the
  // desktop it reused on `?uid=`, so a crafted link could name someone else's;
  // it now takes the person only from the shell's verified voiceAuth answer.
  const params = new URLSearchParams({
    platform: 'prest0',
    vm: cleanBase,
    [CACHE_BUSTER_PARAM]: realtimeCacheBuster(),
  });
  return `${cleanBase}/public/preston-realtime.html?${params.toString()}`;
}

/**
 * The page's own diagnostics overlay (2026-10-10): its boot script shows the audio path, the
 * playback queue, underruns and the mouth's shape when ITS address says `debug=1`. The shell builds
 * that address, so `/desktop?debug=1` never reached it and a live check of the lip sync had no
 * numbers. The flag is carried over from the shell's own address to the frame's, after the target
 * is built (never into the warm cache's copy), and changes nothing but what the person's own screen
 * shows. Without it the frame's address is the one it was, byte for byte.
 */
const PAGE_DEBUG_PARAM = 'debug';

function shellAsksForPageDebug(shellSearch: string): boolean {
  try {
    return new URLSearchParams(shellSearch).get(PAGE_DEBUG_PARAM) === '1';
  } catch {
    return false;
  }
}

export function frameAddress(embedSrc: string | null, shellSearch: string): string | null {
  if (!embedSrc || !shellAsksForPageDebug(shellSearch)) return embedSrc;
  try {
    const u = new URL(embedSrc);
    u.searchParams.set(PAGE_DEBUG_PARAM, '1');
    return u.toString();
  } catch {
    return embedSrc;
  }
}
